/**
 * プロジェクトを人に渡す（第 1a 段・2026-10-21 core/handoff-1a）の英語の文言。en.ts が `...enTransfer` で取り込む（ja.transfer.ts が同じキーを全部持つことを型で強制）。
 * 使う画面: pages/TransferPage.tsx（渡す）・pages/TransferIncomingPage.tsx（受け取る）・components/IncomingTransfers.tsx（プロジェクトの一覧の上の知らせ）。
 *
 * 言葉の決め事（plainWords.test.ts）: 中の言葉（API・secret key・environment・production・deploy・token・developer）を使わない。
 *   sk_ = 「server-only keys (starting with sk_)」・pk_ = 「keys in your app (starting with pk_)」・AI の接続 = 「AI agents」。
 *   ダッシュボードの呼び方どおり「users」（遊ぶ人もアプリの利用者も）・「project」。会社名・個人名は入れない（指示書）。
 * WHY 「何が移るか / 残るか / 外れるか」を 3 つの短い一覧にするか（指示書 4「1 画面で」）: 受託のクライアントは KUMODeck を初めて見る人が多い。
 *   長い説明より「止まらずに移る物」「前の持ち主に残る物（お金）」「外れて作り直す物（鍵）」の 3 つで、受け取った後に何をすればいいかが分かる。
 * WHY 取り消せないことを確認のダイアログで言い切るか: founder の決定 ③（受け取った後に取り消せる待ち時間は置かない）。戻すには新しい受け渡しが要る。
 */
export const enTransfer = {
  'nav.transfer': 'Hand over',

  // 渡す画面
  'transfer.title': 'Hand over this project',
  'transfer.lead':
    'Give this project to someone else — for example the client you built it for. They only need a {product} account (no hosting, code or server accounts of their own). When they accept, they become the owner and you can no longer open the project.',
  'transfer.whatTitle': 'What happens when they accept',
  'transfer.whatTitleIncoming': 'What happens when you accept',
  'transfer.moves.title': 'Moves to the new owner and keeps running',
  'transfer.stays.title': 'Stays with you',
  'transfer.stays.titleIncoming': 'Stays with the current owner',
  'transfer.redo.title': 'Turned off or removed at the handover',
  'transfer.item.games_and_urls': 'The published app, its web address and every published version',
  'transfer.item.players': 'The project’s users, their saved data and their sign-ins',
  'transfer.item.functions_and_data': 'Functions, databases and stored files',
  'transfer.item.public_keys': 'Keys in the app (starting with pk_), so nothing stops',
  'transfer.item.custom_domains': 'The project’s own domains (the DNS records stay where they are)',
  'transfer.item.sign_in_settings': 'Sign-in settings (Google, Discord, Apple) — the new owner should replace them with their own',
  'transfer.item.future_earnings': 'Earnings and usage costs from the moment of the handover',
  'transfer.item.future_usage': 'Usage costs from the moment of the handover',
  'transfer.item.prepaid_balance': 'Prepaid balance',
  'transfer.item.earnings_before': 'Earnings up to the handover, including earnings still on hold',
  'transfer.item.unpaid_usage_before': 'Paying for usage up to the handover',
  'transfer.item.payout_account': 'Payout account',
  'transfer.item.secret_keys': 'Server-only keys (starting with sk_) stop working; the new owner gets new ones',
  'transfer.item.ai_connections': 'AI agents the current owner connected lose access to this project',
  'transfer.item.store_keys': 'App store keys are removed (they belong to the store account holder)',
  'transfer.item.ads_txt': 'ads.txt lines are removed (they name the current owner’s ad account)',
  'transfer.item.scheduled_mail': 'Scheduled emails to users are stopped',
  'transfer.details.title': 'The new owner should replace these with their own',
  'transfer.details.secrets': 'Values your Functions use: {names}',
  'transfer.details.signIn': 'Sign-in settings: {names}',
  'transfer.details.domains': 'Your own domains (confirm them again within 30 days): {names}',
  'transfer.details.storeKeys': 'App store keys (removed at the handover): {names}',
  'transfer.invite.title': 'Invite the new owner',
  'transfer.invite.label': 'Their email address',
  'transfer.invite.help': 'They accept by signing in to {product} (or creating an account) with this address. The invitation is open for {days} days.',
  'transfer.invite.send': 'Send invitation',
  'transfer.invite.sending': 'Sending…',
  'transfer.invite.invalid': 'Enter a full email address.',
  'transfer.invite.self': 'This is your own email address. Enter the address of the person who should receive the project.',
  'transfer.invite.sent': 'Invitation sent to {email}.',
  'transfer.noAccess': 'After the handover you can no longer open this project. To keep helping with it, choose to stay on as a collaborator below (or ask the new owner to add you later).',
  'transfer.pending.title': 'Waiting for {email} to accept',
  'transfer.pending.body': 'You stay the owner until they accept. The invitation is open until {date}.',
  'transfer.pending.cancel': 'Cancel invitation',
  'transfer.cancel.confirmTitle': 'Cancel this invitation?',
  'transfer.cancel.confirmBody': 'They can no longer accept it, and they get an email. You can invite someone again afterwards.',
  'transfer.cancel.done': 'Invitation cancelled.',
  'transfer.hold': 'Your account had a recent security change (for example a new password). To keep your projects safe, you can hand over a project from {date}.',

  // 受け取る画面
  'transfer.in.title': 'Receive “{project}”',
  'transfer.in.from': '{email} wants to hand this project over to you. If you accept, you become its owner.',
  'transfer.in.money': 'From the moment you accept, hosting and usage for this project are paid from your prepaid balance.',
  'transfer.in.addFunds': 'Add funds',
  'transfer.in.expires': 'Open until {date}.',
  'transfer.in.accept': 'Accept project',
  'transfer.in.accepting': 'Accepting…',
  'transfer.in.decline': 'Decline',
  'transfer.in.confirmTitle': 'Accept “{project}”?',
  'transfer.in.confirmBody': 'You become the owner right away. This cannot be undone: to give the project back, you would hand it over again.',
  'transfer.in.declineTitle': 'Decline this project?',
  'transfer.in.declineBody': 'The current owner keeps it and gets an email.',
  'transfer.in.declined': 'You declined. Nothing changed.',
  'transfer.in.done.title': 'You now own “{project}”',
  'transfer.in.done.keys': 'Your new server-only keys (starting with sk_). Copy them now — they are shown only once. The previous owner’s keys no longer work.',
  'transfer.in.done.redeploy': 'Publish your Functions again so they get a new key ({where}).',
  'transfer.in.done.open': 'Open the project',
  'transfer.in.wrongAccount': 'This handover is for {email}. Sign out, then sign in with that address and open the link again.',
  'transfer.in.notFound': 'This handover link is not valid. Ask the owner to send the invitation again.',
  'transfer.blocker.add_funds': 'Add funds to your prepaid balance before you accept.',
  'transfer.blocker.confirm_email': 'Confirm your email address first: open the link in the email {product} sent you.',
  'transfer.blocker.account_closed': 'This account is closed, so it cannot receive a project.',
  'transfer.blocker.project_unavailable': 'This project was deleted or its owner changed, so it can no longer be accepted.',
  'transfer.blocker.owner_security_hold': 'The current owner had a recent security change on their account. You can accept from {date}.',
  'transfer.blocker.other': 'You cannot accept this project right now.',
  'transfer.status.invited': 'This invitation is open.',
  'transfer.status.accepted': 'This project was accepted.',
  'transfer.status.declined': 'This invitation was declined.',
  'transfer.status.cancelled': 'The owner cancelled this invitation.',
  'transfer.status.expired': 'This invitation expired. Ask the owner to send it again.',

  // プロジェクトの一覧の上の知らせ
  'transfer.banner.title': 'A project is waiting for you',
  'transfer.banner.item': '“{project}” from {email}',
  'transfer.banner.review': 'Review',

  // エラー（画面が overrides で引く）
  'transfer.error.pending': 'This project already has an open invitation. Cancel it first to invite someone else.',
  'transfer.error.closed': 'This handover is no longer open.',
  'transfer.error.funds': 'Add funds to your prepaid balance first, then accept.',
  'transfer.error.hold': 'A recent security change on the account blocks the handover for now. Try again after 72 hours.',
  'transfer.error.generic': 'Something went wrong. Wait a moment and try again.'
} as const;
